import Icon from "@components/Atoms/Icon";
import LinksBreadcrumb from "@components/Breadcrumbs/LinksBreadcrumb";
import ArticlePropsService from "@core-ui/ContextServices/ArticleProps";
import IsMobileService from "@core-ui/ContextServices/isMobileService";
import PageDataContextService from "@core-ui/ContextServices/PageDataContext";
import useWatch from "@core-ui/hooks/useWatch";
import { cn } from "@core-ui/utils/cn";
import { cssMedia } from "@core-ui/utils/cssUtils";
import styled from "@emotion/styled";
import getArticleItemLink from "@ext/article/LinkCreator/logic/getArticleItemLink";
import { useDiffViewMode } from "@ext/git/core/Diff/components/store/DiffViewModeStore";
import { useIsDiffView } from "@ext/git/core/Diff/logic/hooks/useIsDiffView";
import ItemMenu from "@ext/item/EditMenu";
import t from "@ext/localization/locale/translate";
import NavigationDropdown from "@ext/navigation/components/NavigationDropdown";
import type { ItemLink } from "@ext/navigation/NavigationLinks";
import Properties from "@ext/properties/components/Helpers/Properties";
import PropertyServiceProvider from "@ext/properties/components/PropertyService";
import { useUpdateArticleProperty } from "@ext/properties/logic/hooks/useUpdateArticleProperty";
import { Button, IconButton } from "@ui-kit/Button";
import { useRef, useState } from "react";

interface ArticleBreadcrumbProps {
	itemLinks: ItemLink[];
	hasPreview: boolean;
	className?: string;
	showActions?: boolean;
}

const ArticleBreadcrumb = ({ className, itemLinks, hasPreview, showActions = true }: ArticleBreadcrumbProps) => {
	const linksRef = useRef<HTMLDivElement>(null);
	const breadcrumbRef = useRef<HTMLDivElement>(null);
	const { articleProperties, setArticleProperties, properties } = PropertyServiceProvider.value;
	const isMobile = IsMobileService.value;
	const diffViewMode = useDiffViewMode();
	const isDiffView = useIsDiffView();

	const [itemLink, setItemLink] = useState<ItemLink>(null);

	const pageData = PageDataContextService.value;
	const articleProps = ArticlePropsService.value;
	const isReadOnly = pageData?.conf.isReadOnly;

	useWatch(() => {
		const newItemLink = getArticleItemLink(itemLinks, articleProps.ref.path);
		setItemLink(newItemLink);
	}, [articleProps.ref.path]);

	const { onSubmit, onDelete } = useUpdateArticleProperty({
		properties: articleProperties,
		setProperties: setArticleProperties,
	});

	const showArticleActions =
		(!articleProps?.errorCode || articleProps?.errorCode === 500) && !!itemLink && showActions;
	const isHasPreview = hasPreview || (diffViewMode === "wysiwyg-double" && isDiffView);

	if (diffViewMode === "single-panel" || diffViewMode === "double-panel") return null;

	return (
		<div className={cn("article-breadcrumb", className, isHasPreview && "has-preview")} ref={breadcrumbRef}>
			<LinksBreadcrumb itemLinks={itemLinks} ref={linksRef} />
			{!isReadOnly && showArticleActions && (
				<>
					<div className="article-actions" data-qa="qa-article-actions">
						<NavigationDropdown
							className="article-actions"
							style={{ marginRight: "-2px" }}
							tooltipText={t("article.actions.title")}
							trigger={
								<Button className="p-0 h-full" size="xs" variant="text">
									<Icon code="ellipsis-vertical" style={{ fontSize: "1.7em" }} />
								</Button>
							}
						>
							<ItemMenu itemLink={itemLink} setItemLink={setItemLink} />
						</NavigationDropdown>
					</div>
				</>
			)}
			<div className="flex min-w-0 max-w-full gap-2 ml-auto flex-row-reverse overflow-hidden">
				<Properties
					catalogProperties={properties}
					hideList={isMobile}
					isReadOnly={isReadOnly || !itemLink}
					onDelete={onDelete}
					onSubmit={onSubmit}
					properties={articleProperties}
					trigger={
						<IconButton
							className="flex-shrink-0"
							data-testid="catalog-properties"
							icon="list-plus"
							size="xs"
							variant="text"
						/>
					}
				/>
			</div>
		</div>
	);
};

export default styled(ArticleBreadcrumb)`
	position: relative;
	display: flex;
	align-items: center;
	flex-wrap: wrap;

	&.has-preview {
		width: 68%;
	}

	.article-actions {
		position: absolute;
		display: flex;
		align-items: center;
		justify-content: end;
		right: 0;
		bottom: -1em;
		margin-right: 4px;
		z-index: var(--z-index-foreground);
		opacity: var(--opacity-darken-element);
	}

	.article-actions i {
		font-size: 22px;
	}

	.article-actions:hover {
		opacity: var(--opacity-active-element);
	}

	${cssMedia.narrow} {
		margin-bottom: 0.25rem;
	}

	@media print {
		display: none;
	}
`;
